import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { chatStorage } from '@/lib/chatStorage';
import { audioManager } from '@/lib/audioManager';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Supabase persists the session under this key (default storageKey format).
// We hydrate it synchronously to avoid a 300–500ms getSession() roundtrip
// blocking first paint for already-signed-in users.
const SUPABASE_STORAGE_KEY = 'sb-yykvfkwwpnfsbugqzuij-auth-token';

function readCachedSession(): Session | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Supabase stores either the session object directly or wrapped in
    // { currentSession, expiresAt }. Handle both shapes defensively.
    const candidate: Session | null =
      parsed?.access_token && parsed?.user
        ? (parsed as Session)
        : parsed?.currentSession ?? null;
    if (!candidate?.access_token || !candidate?.user) return null;
    // Trust only non-expired tokens. expires_at is unix seconds.
    const expiresAt = candidate.expires_at;
    if (expiresAt && expiresAt * 1000 < Date.now()) return null;
    return candidate;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hydrate synchronously from localStorage so authenticated users skip the
  // auth roundtrip on cold load. onAuthStateChange + getSession revalidate
  // in the background and correct state if the server disagrees.
  const initialSession = readCachedSession();
  const [user, setUser] = useState<User | null>(initialSession?.user ?? null);
  const [session, setSession] = useState<Session | null>(initialSession);
  const [loading, setLoading] = useState(!initialSession);

  useEffect(() => {
    // Security: Clear OAuth tokens from URL fragment immediately
    const clearSensitiveUrlParams = () => {
      if (window.location.hash && window.location.hash.includes('access_token')) {
        // Replace current URL without the hash fragment
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Security: Only log non-sensitive info in production
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Auth event:', event);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Clear URL tokens after successful auth
        if (event === 'SIGNED_IN') {
          clearSensitiveUrlParams();

          // NOTE: Automatic test-account reset on every sign-in has been DISABLED.
          // It caused destructive race conditions (the SPA redirected mid-wipe
          // and landed on dead project IDs, trapping the user in spinners).
          // Reset is now a deliberate action surfaced via the
          // `/auth?reset=1` flow or the admin tooling, not an implicit
          // side-effect of authentication.
          //
          // We DO still purge stale local caches so a user who previously had
          // the auto-reset behavior gets a clean slate on next sign-in.
          try {
            if (session?.user?.id) {
              localStorage.removeItem(`verithos_projects_cache_${session.user.id}`);
            }
          } catch {
            /* non-blocking */
          }

          // Log OAuth sign-in/sign-up events (email auth is logged in Auth.tsx)
          if (session?.user) {
            try {
              const provider = session.user.app_metadata?.provider || 'unknown';
              // Only log OAuth sign-ins here; email auth is logged in Auth.tsx
              if (provider === 'email') throw 'skip';

              const dedupKey = `auth_event_logged_${session.user.id}`;
              if (sessionStorage.getItem(dedupKey)) throw 'skip';

              const method = provider === 'google' ? 'google' : provider;
              const createdAt = new Date(session.user.created_at).getTime();
              const isNewUser = Date.now() - createdAt < 10_000;

              const { logEvent: logEvt } = await import('@/lib/analytics/eventLog');
              const { track: trackEvt } = await import('@/lib/analytics/mixpanel');
              const { detectDevice } = await import('@/lib/detectDevice');

              const eventName = isNewUser ? 'User Signed Up' : 'User Signed In';
              const device = detectDevice();
              const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
              trackEvt(eventName, { method, device, user_agent: ua });
              logEvt(eventName, { method, device, user_agent: ua });

              sessionStorage.setItem(dedupKey, '1');
            } catch { /* non-blocking / skip */ }
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error && import.meta.env.DEV) {
        console.error('[AuthContext] Session error');
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Also clear on initial load if tokens present
      clearSensitiveUrlParams();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // CRITICAL: Sign-out must be FAST and INDEPENDENT of any backend cleanup.
    // Previously this awaited `reset-test-user`, which could hang and trap
    // users on `hello@verithos.co` with no escape.

    // 1. Stop audio + clear local caches synchronously.
    audioManager.stop();
    chatStorage.clearAll();
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (
          k === 'verithos_active_project' ||
          k === 'verithos_focus_unlocked' ||
          k.startsWith('verithos_projects_cache_')
        ) {
          localStorage.removeItem(k);
        }
      });
    } catch {
      /* best-effort */
    }

    // 2. Clear local React state immediately so the UI flips to signed-out.
    setUser(null);
    setSession(null);

    // 3. Fire-and-forget the actual Supabase sign-out — never block on it.
    supabase.auth.signOut().catch(() => {
      console.warn('[AuthContext] Sign out may not have completed on server');
    });
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
